import moment from "moment";
import { useCrossColumnRowSync } from "../../../../hooks/useCrossColumnRowSync";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import { formatVolume } from "../../../../utils/volume-helpers";
import { calculateDayColumnWidths } from "../../../../utils/calendar-column-widths";
import WeekDay from "./WeekDay";

const {
  DATE_FORMATS: { DATE_DAY_FORMAT },
} = require("../../../../constants");

const MultiWeekView = ({
  activeWeek,
  calendarRange,
  onDayClick,
  className,
  scrollRef,
  alignDays = false,
  editMode = false,
  isHorizontal = false,
  isTrendsOn = false,
}) => {
  const { locals } = useRillContext();
  const {
    activeUser,
    dailyWorkoutSummaries = {},
    workoutNotes,
    workoutCoachNotes,
    scheduledSessions = {},
    scheduledSections = {},
  } = locals;

  const units = activeUser?.units || "imperial";

  const rowSyncRef = useCrossColumnRowSync(alignDays, "data-mw-sync-row");
  // Horizontal mode has no per-day-of-week toggle (unlike "Align Days"): each
  // week renders its own independent 7-column grid, so a week with wider
  // content (long workout/program names, more set data) grows while sibling
  // weeks stay narrow, leaving headers and day-card rows ragged. Always sync
  // widths in horizontal mode so every week matches the widest one.
  const colSyncRef = useCrossColumnRowSync(
    isHorizontal,
    "data-mw-sync-col",
    "width"
  );
  // The header's own width comes from `.mw-week-col`'s `width: max-content`,
  // which is only as reliable as the browser's propagation of the day-grid's
  // (now-widened) intrinsic size back up through a nested grid — not
  // guaranteed. Pair each header directly with its own week's day-grid (keyed
  // by weekNum, exactly 2 elements per group) so the header's width is set
  // deterministically from the grid's actual rendered width, same as the
  // day-cell sync above.
  const hdrSyncRef = useCrossColumnRowSync(
    isHorizontal,
    "data-mw-sync-wk",
    "width"
  );
  const mergedRef = (node) => {
    if (typeof scrollRef === "function") scrollRef(node);
    else if (scrollRef) scrollRef.current = node;
    rowSyncRef.current = node;
    colSyncRef.current = node;
    hdrSyncRef.current = node;
  };

  const weeks = Array.from({ length: calendarRange }, (_, wi) => {
    const weekStart = activeWeek.clone().add(wi * 7, "days");
    const isCurrent = moment().isBetween(
      weekStart,
      weekStart.clone().add(6, "days"),
      "day",
      "[]"
    );

    const days = Array.from({ length: 7 }, (_, di) => {
      const date = weekStart.clone().add(di, "days");
      const dayString = date.format(DATE_DAY_FORMAT);
      const summary = dailyWorkoutSummaries[dayString];
      const note = workoutNotes?.notes?.find(
        (n) =>
          date.format("YYYY-MM-DD") === moment.utc(n.date).format("YYYY-MM-DD")
      );
      const coachNote = workoutCoachNotes?.notes?.find(
        (n) =>
          date.format("YYYY-MM-DD") === moment.utc(n.date).format("YYYY-MM-DD")
      );
      const exercises = scheduledSessions[dayString];
      const sections = scheduledSections[dayString] || [];
      const hasWorkout = Boolean(exercises?.length) || Boolean(sections.length);
      return { date, dayString, summary, note, coachNote, hasWorkout };
    });

    const weekCompletedExercises = days.reduce(
      (acc, d) => acc + (d.summary?.completedExercises || 0),
      0
    );
    const weekPlannedExercises = days.reduce(
      (acc, d) => acc + (d.summary?.plannedExercises || 0),
      0
    );
    const weekTotalVolume = days.reduce(
      (acc, d) => acc + (d.summary?.totalVolume || 0),
      0
    );
    const weekCompPct =
      weekPlannedExercises > 0
        ? Math.round((weekCompletedExercises / weekPlannedExercises) * 100)
        : 0;

    return {
      weekStart,
      weekNum: wi + 1,
      dateRange: `${weekStart.format("MMM D")} – ${weekStart.clone().add(6, "days").format("MMM D, YYYY")}`,
      days,
      weekTotalVolume,
      weekCompPct,
      isCurrent,
      isEmptyWeek: days.every((d) => !d.hasWorkout),
    };
  });

  // Union "has workout" across every visible week, per weekday — a rest
  // Tuesday should render at the same width as a busy Tuesday in another
  // week of this range, mirroring the month view's own cross-week union
  // (calculateEmptyDayColumns). One shared columnWidths array is then applied
  // to every week row so they all stay aligned, rather than each row sizing
  // itself independently. Edit mode uses the wider ambient "full" column
  // value so DayEditor previews stay usable, matching .wk-edit-mode's CSS.
  const unionHasWorkout = Array.from({ length: 7 }, (_, di) =>
    weeks.some((week) => week.days[di].hasWorkout)
  );
  const columnWidths = isHorizontal
    ? calculateDayColumnWidths(
        unionHasWorkout,
        editMode
          ? // Wider floor than read-only's 7.5rem: a DayEditor's own "+ Add
            // Exercise" button needs more room than a bare REST card. +32px
            // over the DayEditor's base 150px: on hover, "+ Add Exercise"/
            // "Import Workout"/"Add Workout Section" all appear at once and
            // were crowding each other at 150px.
            { fullColumn: "minmax(18rem, 27rem)", emptyColumn: "182px" }
          : isTrendsOn
            ? { fullColumn: "minmax(15rem, 27rem)", emptyColumn: "7.5rem" }
            : { fullColumn: "minmax(12rem, 19.5rem)", emptyColumn: "7.5rem" }
      )
    : null;

  return (
    <div ref={mergedRef} className={`mw-cols ${className || ""}`}>
      {weeks.map((week) => (
        <div
          key={week.weekNum}
          className={`mw-week-col${
            week.isEmptyWeek && !isHorizontal ? " is-empty-week" : ""
          }`}
        >
          <div
            className={`mw-wk-hdr${week.isCurrent ? " is-cur" : ""}`}
            data-mw-sync-wk={isHorizontal ? week.weekNum : undefined}
          >
            {/* Inner wrapper is pinned to the left of the screen in horizontal
                mode (see .mw-wk-hdr-inner) so "Week X" stays visible while the
                day row scrolls sideways. */}
            <div className="mw-wk-hdr-inner">
              <div className="mw-wk-label">Week {week.weekNum}</div>
              <div className="mw-wk-dates">{week.dateRange}</div>
              {(week.weekTotalVolume > 0 || week.weekCompPct > 0) && (
                <div className="mw-wk-stats">
                  {week.weekTotalVolume > 0 && (
                    <span>{formatVolume(week.weekTotalVolume, units)}</span>
                  )}
                  {week.weekCompPct > 0 && (
                    <span>{week.weekCompPct}% done</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div
            className="mw-week-days"
            data-mw-sync-wk={isHorizontal ? week.weekNum : undefined}
            style={
              columnWidths
                ? { gridTemplateColumns: columnWidths.join(" ") }
                : undefined
            }
          >
            {week.days.map(({ date, dayString, note, coachNote }, di) => (
              <WeekDay
                key={dayString}
                date={date}
                notes={note}
                coachNotes={coachNote}
                editMode={editMode}
                syncRowIndex={alignDays ? di : undefined}
                syncColIndex={isHorizontal ? di : undefined}
                onDayClick={
                  onDayClick ? (d) => onDayClick(d, week.weekStart) : undefined
                }
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MultiWeekView;
